import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Modal, SearchBox, Icon } from 'tea-component';
import { history } from 'umi';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import HighlightText from '@intelligence/components/HighlightText';
import NoData from '@intelligence/components/NoData';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { MINI_GAME_DETAIL_PAGE_PATH } from '@/pages/intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';
import type { IProductItem, IProductListModalProps } from './types';
import './index.scss';

// 静态数据源图标提取到模块层，避免每次卡片渲染重复获取
const WEIXIN_ICON = getDataSourceIcon('weixin');
const DOUYIN_ICON = getDataSourceIcon('douyin');

/** 从产品数据中提取展示信息 */
export const resolveProductInfo = (product: IProductItem, isEN: boolean) => {
  if (typeof product === 'string') {
    return {
      id: product,
      name: product,
      cover: '',
      description: '',
      wechatPublisher: '',
      douyinPublisher: '',
      genericPublisher: '',
    };
  }

  const name =
    (isEN
      ? product?.name_en || product?.game_name_en || product?.app_name_en
      : product?.name_cn || product?.game_name_cn || product?.app_name_cn) ||
    product?.name ||
    product?.game_name ||
    product?.app_name ||
    product?.id ||
    '';

  const id =
    product?.game_id || product?.id || product?.app_id || product?.unified_id || '';

  const cover =
    product?.app_cover ||
    product?.cover ||
    product?.icon ||
    product?.img ||
    product?.logo ||
    '';

  const description =
    (isEN
      ? product?.description_en ||
        product?.desc_en ||
        product?.game_desc_en ||
        product?.app_desc_en
      : product?.description_cn ||
        product?.desc_cn ||
        product?.game_desc_cn ||
        product?.app_desc_cn) ||
    product?.description ||
    product?.desc ||
    product?.game_desc ||
    product?.app_desc ||
    product?.summary ||
    '';

  const wechatPublisher =
    (isEN
      ? product?.wechat_publisher_en || product?.wechat_developer_en
      : product?.wechat_publisher_cn || product?.wechat_developer_cn) ||
    product?.wechat_publisher ||
    product?.wechat_developer ||
    product?.publishers?.wechat ||
    product?.publishers?.weixin ||
    (product?.platforms?.includes('wechat') ||
    product?.platforms?.includes('weixin') ||
    product?.wechat_app_id
      ? (isEN
        ? product?.publisher_en || product?.developer_en
        : product?.publisher_cn || product?.developer_cn) ||
        product?.publisher ||
        product?.developer
      : '') ||
    '';

  const douyinPublisher =
    (isEN
      ? product?.douyin_publisher_en || product?.douyin_developer_en
      : product?.douyin_publisher_cn || product?.douyin_developer_cn) ||
    product?.douyin_publisher ||
    product?.douyin_developer ||
    product?.publishers?.douyin ||
    (product?.platforms?.includes('douyin') || product?.douyin_app_id
      ? (isEN
        ? product?.publisher_en || product?.developer_en
        : product?.publisher_cn || product?.developer_cn) ||
        product?.publisher ||
        product?.developer
      : '') ||
    '';

  const genericPublisher =
    (isEN
      ? product?.publisher_en || product?.developer_en
      : product?.publisher_cn || product?.developer_cn) ||
    product?.publisher ||
    product?.developer ||
    '';

  return {
    id,
    name,
    cover,
    description,
    wechatPublisher,
    douyinPublisher,
    genericPublisher,
  };
};

/** 单个产品卡片组件 */
const ProductCard: React.FC<{
  product: IProductItem;
  info: ReturnType<typeof resolveProductInfo>;
  searchKey: string;
  onSelect: (product: IProductItem, info: ReturnType<typeof resolveProductInfo>) => void;
}> = React.memo(({ product, info, searchKey, onSelect }) => {
  const { name, cover, description, wechatPublisher, douyinPublisher, genericPublisher } = info;

  const handleClick = useCallback(() => {
    onSelect(product, info);
  }, [onSelect, product, info]);

  const renderPublishers = () => {
    // 微信与抖音为同一发行商时合并展示双平台图标
    if (wechatPublisher && douyinPublisher && wechatPublisher === douyinPublisher) {
      return (
        <div className="dbi-product-card__publishers">
          <img src={WEIXIN_ICON} alt="wechat" className="dbi-product-card__platform-icon" />
          <img src={DOUYIN_ICON} alt="douyin" className="dbi-product-card__platform-icon" />
          <span className="dbi-product-card__publisher-name">
            <HighlightText text={wechatPublisher} searchKey={searchKey} color="var(--color-primary-5)" />
          </span>
        </div>
      );
    }

    // 微信与抖音发行商不同时分列展示
    if (wechatPublisher && douyinPublisher) {
      return (
        <div className="dbi-product-card__publishers">
          <div className="dbi-product-card__pub-group">
            <img src={WEIXIN_ICON} alt="wechat" className="dbi-product-card__platform-icon" />
            <span className="dbi-product-card__publisher-name">
              <HighlightText text={wechatPublisher} searchKey={searchKey} color="var(--color-primary-5)" />
            </span>
          </div>
          <span className="dbi-product-card__divider">|</span>
          <div className="dbi-product-card__pub-group">
            <img src={DOUYIN_ICON} alt="douyin" className="dbi-product-card__platform-icon" />
            <span className="dbi-product-card__publisher-name">
              <HighlightText text={douyinPublisher} searchKey={searchKey} color="var(--color-primary-5)" />
            </span>
          </div>
        </div>
      );
    }

    if (wechatPublisher) {
      return (
        <div className="dbi-product-card__publishers">
          <div className="dbi-product-card__pub-group">
            <img src={WEIXIN_ICON} alt="wechat" className="dbi-product-card__platform-icon" />
            <span className="dbi-product-card__publisher-name">
              <HighlightText text={wechatPublisher} searchKey={searchKey} color="var(--color-primary-5)" />
            </span>
          </div>
        </div>
      );
    }

    if (douyinPublisher) {
      return (
        <div className="dbi-product-card__publishers">
          <div className="dbi-product-card__pub-group">
            <img src={DOUYIN_ICON} alt="douyin" className="dbi-product-card__platform-icon" />
            <span className="dbi-product-card__publisher-name">
              <HighlightText text={douyinPublisher} searchKey={searchKey} color="var(--color-primary-5)" />
            </span>
          </div>
        </div>
      );
    }

    if (genericPublisher) {
      return (
        <div className="dbi-product-card__publishers">
          <span className="dbi-product-card__publisher-name">
            <HighlightText text={genericPublisher} searchKey={searchKey} color="var(--color-primary-5)" />
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dbi-product-card" onClick={handleClick}>
      {cover ? (
        <img src={cover} alt={name} className="dbi-product-card__avatar" />
      ) : (
        <div className="dbi-product-card__avatar" />
      )}
      <div className="dbi-product-card__info">
        <div className="dbi-product-card__title">
          <span className="dbi-product-card__title-text">
            <HighlightText text={name} searchKey={searchKey} color="var(--color-primary-5)" />
          </span>
          <span className="dbi-product-card__arrow">&gt;</span>
        </div>
        {description && (
          <div className="dbi-product-card__desc">
            <HighlightText text={description} searchKey={searchKey} color="var(--color-primary-5)" />
          </div>
        )}
        {renderPublishers()}
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

/**
 * 产品列表弹窗组件（同发行商或公司旗下游戏列表）
 */
const ProductListModal: React.FC<IProductListModalProps> = ({
  visible,
  onClose,
  publisher,
  products = [],
  onProductClick,
  title,
  className,
}) => {
  const { formatMessage, isEN } = useIntlExtend();
  const [searchKey, setSearchKey] = useState('');

  // 弹窗关闭时清理搜索词
  useEffect(() => {
    if (!visible) {
      setSearchKey('');
    }
  }, [visible]);

  // 预先解析产品信息并进行搜索过滤，单次循环完成
  const resolvedAndFilteredList = useMemo(() => {
    const rawList = Array.isArray(products) ? products : [];
    const resolved = rawList.map((p) => ({
      product: p,
      info: resolveProductInfo(p, isEN),
    }));

    if (!searchKey.trim()) return resolved;

    const lowerKey = searchKey.trim().toLowerCase();
    return resolved.filter(({ info }) => {
      return (
        info.name.toLowerCase().includes(lowerKey) ||
        info.description.toLowerCase().includes(lowerKey) ||
        info.wechatPublisher.toLowerCase().includes(lowerKey) ||
        info.douyinPublisher.toLowerCase().includes(lowerKey) ||
        info.genericPublisher.toLowerCase().includes(lowerKey)
      );
    });
  }, [products, searchKey, isEN]);

  const handleProductClick = useCallback(
    (product: IProductItem, info: ReturnType<typeof resolveProductInfo>) => {
      if (onProductClick) {
        onProductClick(product);
      } else {
        const targetId =
          info.id ||
          (product as any)?.game_id ||
          (product as any)?.id ||
          (product as any)?.app_id;

        if (targetId) {
          const currentPath = history.location.pathname;
          // 如果当前已经在详情页系列路径下，替换 ID；否则跳转至配置的默认详情页路径
          if (currentPath.includes('/gamesDetails')) {
            const basePath = currentPath.substring(
              0,
              currentPath.indexOf('/gamesDetails') + '/gamesDetails'.length,
            );
            history.push(`${basePath}/${targetId}`);
          } else {
            history.push(`${MINI_GAME_DETAIL_PAGE_PATH}/${targetId}`);
          }
        }
      }
      onClose();
    },
    [onProductClick, onClose],
  );

  return (
    <Modal
      visible={visible}
      caption={undefined}
      onClose={onClose}
      disableCloseIcon={true}
      className={`dbi-product-list-modal ${className || ''}`}
    >
      <Modal.Body>
        <div className="dbi-product-list-modal__header">
          <div className="dbi-product-list-modal__title-wrap">
            <span className="dbi-product-list-modal__title">
              {title || formatMessage({ id: 'dbi_product_list' })}
            </span>
            {publisher && (
              <span className="dbi-product-list-modal__publisher">{publisher}</span>
            )}
          </div>
          <button
            type="button"
            className="dbi-product-list-modal__close-btn"
            onClick={onClose}
            aria-label={formatMessage({ id: 'dbi_close' }) || 'close'}
          >
            <Icon type="close" />
          </button>
        </div>

        <SearchBox
          className="dbi-product-list-modal__search"
          size="full"
          placeholder={formatMessage({ id: 'dbi_default_search_placeholder' })}
          value={searchKey}
          onChange={setSearchKey}
          onClear={() => setSearchKey('')}
        />

        {resolvedAndFilteredList.length > 0 ? (
          <div className="dbi-product-list-modal__list">
            {resolvedAndFilteredList.map(({ product, info }, index) => (
              <ProductCard
                key={info.id || `${info.name}-${index}`}
                product={product}
                info={info}
                searchKey={searchKey}
                onSelect={handleProductClick}
              />
            ))}
          </div>
        ) : (
          <div className="dbi-product-list-modal__empty">
            <NoData text={formatMessage({ id: 'dbi_no_data' })} />
          </div>
        )}

        {resolvedAndFilteredList.length > 0 && (
          <div className="dbi-product-list-modal__count">
            {formatMessage(
              {
                id:
                  resolvedAndFilteredList.length === 1
                    ? 'dbi_product_list_total_count_singular'
                    : 'dbi_product_list_total_count',
              },
              { count: resolvedAndFilteredList.length },
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export * from './types';
export default ProductListModal;
